import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtilService } from '@services/http-util.service';
import { EnvironmentService } from '@services/environment.service';
import { Injectable } from '@angular/core';
import { enc, SHA256 } from 'crypto-js';
import { map, Observable } from 'rxjs';

export interface Pkce {
  method: 'S256';
  challenge: string;
  verifier: string;
  integrationId?: string;
  currentRoute?: string;
}

export interface ExchangeResponse {
  state: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  environment_id: string;
  environment_name: string;
  legal_entity_id: string;
  legal_entity_name: string;
  user_id: string;
  email: string;
  family_name: string;
  given_name: string;
  mode: string;
  scope: string;
}

export interface AccessToken {
  access_token: string;
  environment_name: string;
  expires: string;
  state: string;
}

export interface AccessTokens {
  tokens: AccessToken[];
}

export interface AuthroizationResponse {
  code: string;
  state: string;
}

@Injectable({
  providedIn: 'root',
})
export class BlackbaudAuthService {
  private clientId: string = '<SKY_APPLICATION_CLIENT_ID>';
  private landingPageUrl = window.location.origin + '/auth-landing';

  constructor(
    private httpClient: HttpClient,
    private http: HttpUtilService,
    private environmentService: EnvironmentService
  ) {}

  public removeState(state: string): void {
    localStorage.removeItem(state);
  }

  public exchangeAuthorizationCode(
    authorizationResponse: AuthroizationResponse
  ): Observable<ExchangeResponse> {
    const stateDetails = this.getStateDetails(authorizationResponse.state);
    if (!stateDetails) {
      throw 'Could not get verifier from local storage using the state';
    }
    const verifier = stateDetails.verifier;
    const body = {
      grant_type: 'authorization_code',
      client_id: stateDetails.client_id,
      redirect_uri: this.landingPageUrl,
      code: authorizationResponse.code,
      code_verifier: verifier,
    };
    // test return just the mock data
    /* return of({
      state: authorizationResponse.state,
      access_token: 'mock_access_token_12345',
      token_type: 'Bearer',
      expires_in: 3600,
      environment_id: 'p-123456',
      environment_name: 'Production',
      legal_entity_id: 'le-789012',
      legal_entity_name: 'Test Organization',
      user_id: 'u-345678',
      email: 'test.user@example.com',
      family_name: 'User',
      given_name: 'Test',
      mode: 'production',
      scope: 'email offline_access',
    });*/
    return this.http
      .request(
        'put',
        `${this.environmentService.environment.apiRoot}/integrations/crm/${stateDetails.name}/exchange-authorization-code`,
        body,
        null,
        true
      )
      .pipe(map(result => result as ExchangeResponse));
  }

  public authorize(integration: any, imports: boolean = false): void {
    this.clientId = integration.json_data.client_id;
    const currentRoute = imports
      ? `imports/edit-integration/${integration.id}`
      : `admin/integrations/edit/${integration.id}`;

    const pkce = this.getPkce(integration, currentRoute);
    const state = this.getState(40);
    const details = {
      name: integration.name,
      id: integration.id,
      client_id: integration.json_data.client_id,
      currentRoute,
      verifier: pkce.verifier,
    };

    localStorage.setItem(state, JSON.stringify(details));
    const locationHref = this.getAuthorizationUrl(state, pkce);
    // mock the code for testing
    /*const mockCode = 'mock_code_12345';
    const mock = this.landingPageUrl + '?code=' + mockCode + '&state=' + state;
    (window as any).location.href = mock;*/
    (window as any).location.href = locationHref;
  }

  private getAuthorizationUrl(state: string, pkce: Pkce): string {
    const params = this.getAuthorizationParams(state, pkce);
    return `https://app.blackbaud.com/oauth/authorize?${params.toString()}`;
  }

  private getAuthorizationParams(state: string, pkce: Pkce): HttpParams {
    const parmas = {
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.landingPageUrl,
      state: state,
      code_challenge_method: pkce.method,
      code_challenge: pkce.challenge,
    };
    return new HttpParams({
      fromObject: parmas,
    });
  }

  public getPkce(integration: any, currentRoute: string): Pkce {
    const possibleChacters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const verifier = this.randomString(possibleChacters, 128);
    const challenge = SHA256(verifier).toString(enc.Base64url);

    return {
      method: 'S256',
      challenge: challenge,
      verifier: verifier,
      integrationId: integration.id,
      currentRoute,
    };
  }
  public randomString(possible: string, length: number): string {
    let text = '';
    const possibleLength = possible.length;
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possibleLength));
    }
    return text;
  }
  public getState(length: number = 40): string {
    const possibleChacters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return this.randomString(possibleChacters, length);
  }

  public getStateDetails(state: string): any {
    const details = localStorage.getItem(state);
    if (!details) {
      return null;
    }
    return JSON.parse(details);
  }
}
